const SELECTORS = {
  section: '.section-service-sidebar',
  footer: '.shopify-section-group-footer-group, .site-footer',
  headerStickyWrapper: '.header-sticky-wrapper',
  headerWrapper: '#HeaderWrapper',
  siteHeader: '#SiteHeader',
  main: 'main#MainContent, main.main-content, main[role="main"], main',
};

const MOBILE_MAX_WIDTH = 768;
const STICKY_CLASS = 'site-header--stuck';
const STICKY_OPENING_CLASS = 'site-header--opening';

class CustomServiceSidebar extends HTMLElement {
  #section = null;
  #header = { stickyWrapper: null, wrapper: null, siteHeader: null };
  #main = null;
  #placeholder = null;
  #frameId = 0;
  #mutationObserver = null;
  #resizeObserver = null;
  #bound = null; 
  #observing = null;

  connectedCallback() {
    this.#section = this.closest(SELECTORS.section);
    if (!this.#section) return;

    this.#placeholder = document.createElement('div');
    this.#placeholder.hidden = true;
    this.#placeholder.setAttribute('data-service-sidebar-placeholder', '');
    this.#section.insertAdjacentElement('afterend', this.#placeholder);

    this.#main = document.querySelector(SELECTORS.main);
    this.#refreshHeaderRefs();
    this.#setupHeaderObservers();

    this.#bound = {
      layout: this.#handleLayoutChange.bind(this),
      scroll: this.#handleScroll.bind(this),
    };

    requestAnimationFrame(() => {
      this.#handleLayoutChange();
      window.addEventListener('load', this.#bound.layout);
      window.addEventListener('resize', this.#bound.layout);
      window.addEventListener('scroll', this.#bound.scroll, { passive: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('load', this.#bound.layout);
    window.removeEventListener('resize', this.#bound.layout);
    window.removeEventListener('scroll', this.#bound.scroll);
    if (this.#frameId) cancelAnimationFrame(this.#frameId);
    this.#disconnectObservers();
  }

  #handleLayoutChange() {
    if (!this.#section) return;
    this.#refreshHeaderRefs();
    this.#setupHeaderObservers();

    if (window.innerWidth <= MOBILE_MAX_WIDTH) {
      this.#moveSectionToMainStart();
      this.#setCssVar('--service-sidebar-height', null);
    } else {
      this.#restoreSectionPosition();
      this.#updateTrackHeight();
    }

    this.#updateStickyOffset();
  }

  #scheduleUpdate() {
    if (this.#frameId || !this.#section) return;
    this.#frameId = requestAnimationFrame(() => {
      this.#frameId = 0;
      this.#updateStickyOffset();
    });
  }

  #handleScroll() { this.#scheduleUpdate(); }

  #updateTrackHeight() {
    const footer = document.querySelector(SELECTORS.footer);
    if (!footer) { this.#setCssVar('--service-sidebar-height', null); return; }
    const height = Math.max(0, this.#docTop(footer) - this.#docTop(this.#section));
    this.#setCssVar('--service-sidebar-height', `${Math.round(height)}px`);
  }

  #updateStickyOffset() {
    this.#setCssVar('--service-sidebar-top', `${Math.round(this.#getStickyOffset())}px`);
  }

  #moveSectionToMainStart() {
    if (!this.#main || (this.#section.parentNode === this.#main && this.#main.firstElementChild === this.#section)) return;
    this.#main.prepend(this.#section);
  }

  #restoreSectionPosition() {
    const parent = this.#placeholder?.parentNode;
    if (!parent || (this.#section.parentNode === parent && this.#section.nextElementSibling === this.#placeholder)) return;
    parent.insertBefore(this.#section, this.#placeholder);
  }

  #docTop(el) {
    return el.getBoundingClientRect().top + window.scrollY;
  }

  #setCssVar(name, value) {
    if (value === null) {
      this.#section.style.removeProperty(name);
    } else if (this.#section.style.getPropertyValue(name) !== value) {
      this.#section.style.setProperty(name, value);
    }
  }

  #refreshHeaderRefs() {
    this.#header = {
      stickyWrapper: document.querySelector(SELECTORS.headerStickyWrapper),
      wrapper: document.querySelector(SELECTORS.headerWrapper),
      siteHeader: document.querySelector(SELECTORS.siteHeader),
    };
  }

  #setupHeaderObservers() {
    const { siteHeader, stickyWrapper } = this.#header;
    if (!siteHeader && !stickyWrapper) { this.#disconnectObservers(); return; }

    // Skip if already observing the same elements
    if (this.#observing?.siteHeader === siteHeader && this.#observing?.stickyWrapper === stickyWrapper) return;

    this.#disconnectObservers();
    const onStateChange = () => this.#scheduleUpdate();

    if (siteHeader) {
      this.#mutationObserver = new MutationObserver(onStateChange);
      this.#mutationObserver.observe(siteHeader, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(onStateChange);
      if (stickyWrapper) this.#resizeObserver.observe(stickyWrapper);
      if (siteHeader) this.#resizeObserver.observe(siteHeader);
    }

    this.#observing = { siteHeader, stickyWrapper };
  }

  #disconnectObservers() {
    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver = null;
    this.#resizeObserver = null;
    this.#observing = null;
  }

  #getStickyOffset() {
    const { stickyWrapper, wrapper, siteHeader } = this.#header;
    const visibleBottom = Math.max(0, stickyWrapper?.getBoundingClientRect().bottom ?? 0);

    const isSticky = siteHeader?.dataset.sticky === 'true';
    const isActive = siteHeader?.classList.contains(STICKY_CLASS) || siteHeader?.classList.contains(STICKY_OPENING_CLASS);

    if (!isSticky || !isActive) return visibleBottom;

    const reserved = Math.max(
      stickyWrapper?.offsetHeight ?? 0,
      wrapper?.offsetHeight ?? 0,
      siteHeader?.offsetHeight ?? 0,
    );

    return Math.max(visibleBottom, reserved);
  }
}

if (!customElements.get('custom-service-sidebar')) {
  customElements.define('custom-service-sidebar', CustomServiceSidebar);
}